import React from 'react';
import {Navigate, Outlet, useLocation} from 'react-router-dom';
import {Container} from 'reactstrap';
import AppNavbar from './AppNavbar';
import SubscriptionBanner from './SubscriptionBanner';
import WhatIfBanner from '../scenarios/WhatIfBanner';
import ResizableAssistantPanel from './ResizableAssistantPanel';
import ScrollToTop, {MAIN_CONTENT_ID} from './ScrollToTop';
import {useAssistantPanel, useAssistantRouteContext} from '../../assistant';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';
import {Bot, ChevronLeft} from 'lucide-react';
import useGetSubscriptionStatus from '../../api/customers/useGetSubscriptionStatus';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import {usePageConfig, useRouteMatch} from '../../hooks/usePageConfig';
import {RoutePaths} from '../../RoutePaths';
import SampleDataBanner from '../main/getting-started/SampleDataBanner';

// Routes that remain reachable when the account is billing-only:
// billing/company details (settings), account, and auth pages.
const BILLING_ONLY_ALLOWED_PREFIXES = [
    RoutePaths.settings.root(),
    RoutePaths.accounts.profile(),
    RoutePaths.accounts.login(),
    RoutePaths.accounts.signUp(),
    RoutePaths.accounts.passwordlessLogin(),
    RoutePaths.accounts.userRegistrationSocial(),
    RoutePaths.accounts.emailConfirmation(),
    RoutePaths.accounts.changePassword(),
    RoutePaths.accounts.forgotPassword(),
    RoutePaths.accounts.resetPassword()
];

/**
 * When enforcement is active and the account is billing-only, redirect any
 * navigation outside billing/account/auth pages to the billing page.
 */
const BillingOnlyGate: React.FC = () => {
    const authStateManager = useAuthStateManager();

    if (!authStateManager.isAuthenticated || !authStateManager.customerId) {
        return null;
    }

    return <BillingOnlyGateInner />;
};

const BillingOnlyGateInner: React.FC = () => {
    const {data: subscription} = useGetSubscriptionStatus();
    const location = useLocation();

    if (!subscription) return null;
    if (!subscription.enforcementActive) return null;
    if (subscription.enforcementMode !== 'BillingOnly') return null;

    const isAllowed = BILLING_ONLY_ALLOWED_PREFIXES.some(prefix =>
        location.pathname.startsWith(prefix)
    );
    if (isAllowed) return null;

    return <Navigate to={RoutePaths.settings.billing()} replace />;
};

function Layout() {
    const {
        isPanelOpen,
        isPanelMinimized,
        isAssistantAvailable,
        minimizePanel,
        restorePanel
    } = useAssistantPanel();
    const showAssistantPanel =
        isPanelOpen && !isPanelMinimized && isAssistantAvailable;
    const page = usePageConfig();
    const route = useRouteMatch();
    const location = useLocation();
    // The route layer of the assistant's page context: every app page reports at
    // least where the user is, before the page itself publishes anything.
    useAssistantRouteContext(
        route?.pattern ?? location.pathname,
        `${location.pathname}${location.search}`,
        route?.title ?? page?.title
    );
    const contentPanelClass = page?.fullBleed
        ? 'contentPanel cv-contentPanel--bleed mx-auto'
        : 'contentPanel pt-2 pb-4 px-3 mx-auto';

    return (
        <div className="appShell">
            <BillingOnlyGate />
            <ScrollToTop />
            <AppNavbar />
            <SubscriptionBanner />
            <WhatIfBanner />
            <SampleDataBanner />
            <div className="mainLayoutContainer">
                <PanelGroup
                    direction="horizontal"
                    className="assistant-panel-group"
                >
                    {' '}
                    <Panel
                        defaultSize={showAssistantPanel ? 75 : 100}
                        minSize={30}
                    >
                        <Container
                            id={MAIN_CONTENT_ID}
                            fluid
                            className={contentPanelClass}
                        >
                            <Outlet />
                        </Container>
                    </Panel>
                    {showAssistantPanel && (
                        <>
                            <PanelResizeHandle className="assistant-resize-handle" />
                            <Panel
                                defaultSize={25}
                                minSize={18}
                                collapsible={true}
                                onCollapse={() => {
                                    if (isPanelOpen) minimizePanel();
                                }}
                                collapsedSize={0}
                                className="assistant-panel-wrapper"
                            >
                                <ResizableAssistantPanel />
                            </Panel>
                        </>
                    )}
                </PanelGroup>
            </div>
            {isPanelOpen && isPanelMinimized && isAssistantAvailable && (
                <button
                    className="assistant-pullout-tab"
                    onClick={restorePanel}
                    aria-label="Show assistant panel"
                    title="Show assistant"
                >
                    <ChevronLeft size={16} />
                    <Bot size={18} />
                    <span className="assistant-pullout-tab-label">
                        Assistant
                    </span>
                </button>
            )}
        </div>
    );
}

export default Layout;
